import { STOCK_INFINITE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS, STOCK_MULTIPLE_ITEMS } from './types';

export const SPIRIT_LISTING_TYPE = 'Spirit-Listing';
export const EQUIPMENT_LISTING_TYPE = 'Equipment-Listing';
export const LEGACY_INFINITE_STOCK = 1000000000000000;

const DERIVED_LISTING_FIELD_KEYS = ['age'];
const SPIRIT_ONLY_LISTING_FIELD_KEYS = [
  'age',
  'barrel_volume',
  'lot_code',
  'producer',
  'sampling_availability',
  'spirit_type',
];

const parseNonNegativeInteger = value => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
      ? Number.parseInt(value, 10)
      : NaN;

  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const parseManufactureYear = (value, referenceDate = new Date()) => {
  const reference = normalizeReferenceDate(referenceDate);
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim().match(/^\d+$/)
      ? Number.parseInt(value.trim(), 10)
      : NaN;

  if (!reference || !Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed <= reference.getUTCFullYear() ? parsed : null;
};

const parseDateOnly = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const [, yearString, monthString, dayString] = match;
  const year = Number(yearString);
  const month = Number(monthString);
  const day = Number(dayString);
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? date
    : null;
};

const normalizeReferenceDate = referenceDate => {
  const reference = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  return Number.isNaN(reference.getTime()) ? null : reference;
};

const formatNumberPart = value => `${value}`.padStart(2, '0');
const getDaysInUTCMonth = (year, monthIndex) =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
const AGE_UNIT_FALLBACKS = {
  year: { one: 'year', other: 'years' },
  month: { one: 'month', other: 'months' },
  day: { one: 'day', other: 'days' },
};

const parseAgeFormatterInput = value => {
  if (typeof value === 'string') {
    return { sourceDate: value, fallbackAge: null };
  }

  if (typeof value === 'number') {
    return { sourceDate: null, fallbackAge: parseNonNegativeInteger(value) };
  }

  return {
    sourceDate: getListingAgeSourceDate(value),
    fallbackAge: parseNonNegativeInteger(value?.age),
  };
};

export const getListingAgeSourceDate = publicData =>
  publicData?.distillationDate || publicData?.fillDate || null;

export const isSpiritListingType = listingType => listingType === SPIRIT_LISTING_TYPE;
export const isEquipmentListingType = listingType => listingType === EQUIPMENT_LISTING_TYPE;
export const isDerivedListingFieldKey = key => DERIVED_LISTING_FIELD_KEYS.includes(key);
export const isSpiritOnlyListingFieldKey = key => SPIRIT_ONLY_LISTING_FIELD_KEYS.includes(key);
export const isValidListingDate = dateString => parseDateOnly(dateString) != null;
export const getUTCDateString = (referenceDate = new Date()) => {
  const reference = normalizeReferenceDate(referenceDate);

  if (!reference) {
    return '';
  }

  return `${reference.getUTCFullYear()}-${formatNumberPart(
    reference.getUTCMonth() + 1
  )}-${formatNumberPart(reference.getUTCDate())}`;
};

export const isFutureListingDate = (dateString, referenceDate = new Date()) => {
  const listingDate = parseDateOnly(dateString);
  const reference = normalizeReferenceDate(referenceDate);

  if (!listingDate || !reference) {
    return false;
  }

  const referenceDateOnly = new Date(
    Date.UTC(reference.getUTCFullYear(), reference.getUTCMonth(), reference.getUTCDate())
  );

  return listingDate.getTime() > referenceDateOnly.getTime();
};

export const formatListingDate = (dateString, intl, options = {}) => {
  if (!dateString || !intl?.formatDate) {
    return '';
  }

  const parsedDate = parseDateOnly(dateString);
  if (!parsedDate) {
    return dateString;
  }

  return intl.formatDate(parsedDate, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    timeZone: 'UTC',
    ...options,
  });
};

export const getListingTypeOverrides = listingType =>
  isSpiritListingType(listingType) ? { stockType: STOCK_MULTIPLE_ITEMS } : {};

export const isListingTypeWithManagedStock = listingTypeConfig => {
  if (isSpiritListingType(listingTypeConfig?.listingType)) {
    return true;
  }

  return [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );
};

export const isListingTypeWithInfiniteStock = listingTypeConfig => {
  if (isSpiritListingType(listingTypeConfig?.listingType)) {
    return false;
  }

  return STOCK_INFINITE_ITEMS.includes(listingTypeConfig?.stockType);
};

export const normalizeManagedStockQuantity = quantity => {
  const parsed = parseNonNegativeInteger(quantity);
  return parsed == null || parsed >= LEGACY_INFINITE_STOCK ? null : parsed;
};

export const getListingAgeParts = (value, referenceDate = new Date()) => {
  const { sourceDate } = parseAgeFormatterInput(value);
  const listingDate = parseDateOnly(sourceDate);
  const reference = normalizeReferenceDate(referenceDate);

  if (!listingDate || !reference) {
    return null;
  }

  const referenceDateOnly = new Date(
    Date.UTC(reference.getUTCFullYear(), reference.getUTCMonth(), reference.getUTCDate())
  );

  if (listingDate.getTime() > referenceDateOnly.getTime()) {
    return null;
  }

  let years = referenceDateOnly.getUTCFullYear() - listingDate.getUTCFullYear();
  let months = referenceDateOnly.getUTCMonth() - listingDate.getUTCMonth();
  let days = referenceDateOnly.getUTCDate() - listingDate.getUTCDate();

  if (days < 0) {
    months -= 1;

    const previousMonthIndex = (referenceDateOnly.getUTCMonth() + 11) % 12;
    const previousMonthYear =
      previousMonthIndex === 11
        ? referenceDateOnly.getUTCFullYear() - 1
        : referenceDateOnly.getUTCFullYear();

    days += getDaysInUTCMonth(previousMonthYear, previousMonthIndex);
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years: Math.max(0, years), months: Math.max(0, months), days: Math.max(0, days) };
};

const formatListingAgeUnit = (value, unit, intl) => {
  const count = Number(value);
  const isSingular = count === 1;
  const pluralCategory = isSingular ? 'one' : 'other';
  const messageId = `ListingAge.${unit}.${pluralCategory}`;
  const unitLabel =
    (intl?.messages && typeof intl.messages[messageId] === 'string' && intl.messages[messageId]) ||
    AGE_UNIT_FALLBACKS[unit]?.[pluralCategory] ||
    `${unit}${isSingular ? '' : 's'}`;

  return `${count} ${unitLabel}`;
};

export const formatListingAge = (value, intl, options = {}) => {
  const { maxUnits = 3, referenceDate = new Date() } = options;
  const { fallbackAge } = parseAgeFormatterInput(value);
  const ageParts = getListingAgeParts(value, referenceDate);
  const parts =
    ageParts || (fallbackAge != null ? { years: fallbackAge, months: 0, days: 0 } : null);

  if (!parts) {
    return '';
  }

  const units = [
    { unit: 'year', value: parts.years },
    { unit: 'month', value: parts.months },
    { unit: 'day', value: parts.days },
  ].filter(part => part.value > 0);

  const unitsToRender = (units.length > 0 ? units : [{ unit: 'day', value: 0 }]).slice(0, maxUnits);

  return unitsToRender.map(part => formatListingAgeUnit(part.value, part.unit, intl)).join(' ');
};

export const calculateListingAge = (dateString, referenceDate = new Date()) => {
  const ageParts = getListingAgeParts(dateString, referenceDate);
  return ageParts ? ageParts.years : null;
};

export const calculateListingAgeFromManufactureYear = (
  manufactureYear,
  referenceDate = new Date()
) => {
  const parsedYear = parseManufactureYear(manufactureYear, referenceDate);
  const reference = normalizeReferenceDate(referenceDate);

  return parsedYear != null && reference ? reference.getUTCFullYear() - parsedYear : null;
};

export const calculateListingAgeFromPublicData = (publicData, referenceDate = new Date()) => {
  if (!publicData || typeof publicData !== 'object') {
    return null;
  }

  if (hasOwn(publicData, 'manufactureYear')) {
    return calculateListingAgeFromManufactureYear(publicData.manufactureYear, referenceDate);
  }

  return calculateListingAge(getListingAgeSourceDate(publicData), referenceDate);
};

export const publicDataWithCalculatedAge = (publicData, referenceDate = new Date()) => {
  if (!publicData || typeof publicData !== 'object') {
    return publicData;
  }

  const hasAgeSource =
    hasOwn(publicData, 'manufactureYear') ||
    hasOwn(publicData, 'distillationDate') ||
    hasOwn(publicData, 'fillDate');

  if (!hasAgeSource) {
    return publicData;
  }

  return {
    ...publicData,
    age: calculateListingAgeFromPublicData(publicData, referenceDate),
  };
};

export const getListingAge = (publicData, referenceDate = new Date()) => {
  const calculatedAge = calculateListingAgeFromPublicData(publicData, referenceDate);
  return calculatedAge != null ? calculatedAge : parseNonNegativeInteger(publicData?.age);
};
